import { ExpandLess, ExpandMore, KeyboardArrowDown } from '@rossum/ui/icons';
import {
  Checkbox,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Skeleton,
  Stack,
  Typography,
} from '@rossum/ui/material';
import { useEffect, useMemo, useState } from 'react';
import * as R from 'remeda';
import SearchInput from '../../../ui/search-input/SearchInput';
import { filterWorkspaces } from '../helpers/filterWorkspaces';
import { highlightSearchedValue } from '../helpers/highlightSearchedValue';

type Workspaces = Array<{
  id: number;
  name: string;
  queues: Array<{ id: number; name: string }>;
}>;

type QueueMultiselectListProps = {
  topSelections?: number[];
  onSelect: (queues: number[]) => void;
  selectedQueues: number[];
  searchInputProps?: { dataCy?: string };
  queuesByWorkspace: Workspaces;
  isLoading?: boolean;
  multiSelect?: boolean;
  dense?: boolean;
  disabled?: boolean;
};

export const QueueSelectList = ({
  topSelections = [],
  searchInputProps,
  onSelect,
  selectedQueues,
  queuesByWorkspace,
  isLoading,
  multiSelect = true,
  dense,
  disabled,
}: QueueMultiselectListProps) => {
  const [search, setSearch] = useState<string>('');
  const [expandedWorkspaces, setExpandedWorkspaces] = useState<number[]>([]);

  const isSearchActive = search.length > 1;

  const workspaces = useMemo<Workspaces>(() => {
    if (!queuesByWorkspace) return [];

    const toNormalize = isSearchActive
      ? filterWorkspaces(search, queuesByWorkspace)
      : queuesByWorkspace;

    return toNormalize
      .filter(({ queues }) => !!queues.length)
      .map(({ name, queues, id }) => ({
        id,
        name,
        queues: queues.map(queue => ({
          id: queue.id,
          name: queue.name,
        })),
      }));
  }, [isSearchActive, queuesByWorkspace, search]);

  const selectedQueuesSet = useMemo(
    () => new Set(selectedQueues),
    [selectedQueues]
  );

  // this is so that we don't have to do nested iterations O(n²) to handle selectedQueues
  const sortedWorkspaces = useMemo(() => {
    const normalizedWorkspaces = (workspaces ?? []).map(workspace => {
      const isFullySelected = !!workspace?.queues.every(({ id }) =>
        selectedQueuesSet.has(id)
      );
      const isPartiallySelected = !!workspace?.queues.some(({ id }) =>
        selectedQueuesSet.has(id)
      );
      return {
        ...workspace,
        isFullySelected,
        isPartiallySelected,
      };
    });

    return R.partition(normalizedWorkspaces, option =>
      option.queues.some(({ id }) => topSelections.includes(id))
    ).flat();
  }, [workspaces, selectedQueuesSet, topSelections]);

  // expand searched workspaces
  useEffect(() => {
    // Expand workspaces only if search input has more than 1 character
    const shouldUpdateExpandedWorkspaces = isSearchActive && workspaces;

    if (shouldUpdateExpandedWorkspaces) {
      const workspacesToExpand = workspaces.map(workspace => workspace.id);

      if (workspacesToExpand.length) {
        setExpandedWorkspaces(prev => [
          ...new Set([...prev, ...workspacesToExpand]),
        ]);
      }
    }

    // Collapse all workspaces if the search is not available
    if (!isSearchActive) setExpandedWorkspaces([]);
  }, [isSearchActive, workspaces, search]);

  const handleSelect = (ids: number[]) => {
    onSelect(ids);
  };

  return (
    <Stack>
      <SearchInput
        disabled={disabled}
        onChange={setSearch}
        value={search}
        withStartAdornment={false}
        sx={{ mb: 2, width: '100%' }}
        slotProps={
          searchInputProps?.dataCy
            ? {
                htmlInput: { 'data-cy': searchInputProps.dataCy },
              }
            : undefined
        }
      />
      {isLoading ? (
        <Stack spacing={0.3} width="100%">
          {[1, 2, 3, 4, 5].map(number => (
            <Stack key={number} flexDirection="row" alignItems="center" gap={2}>
              <Checkbox disabled sx={{ p: 0 }} />
              <Skeleton height={dense ? 30 : 56} width="100%" variant="text" />
              <KeyboardArrowDown />
            </Stack>
          ))}
        </Stack>
      ) : (
        <Stack spacing={1} width="100%">
          <List
            disablePadding={dense}
            dense={dense}
            sx={{ maxHeight: 'min(600px, 40vh)', overflow: 'auto' }}
          >
            {sortedWorkspaces?.map(workspace => {
              return (
                <Stack key={workspace.id}>
                  <ListItem
                    disablePadding={dense}
                    disableGutters={dense}
                    sx={dense ? { my: 0.5 } : {}}
                  >
                    {multiSelect ? (
                      <ListItemIcon sx={{ minWidth: 0, mr: 1 }}>
                        <Checkbox
                          disabled={disabled}
                          indeterminate={
                            !workspace.isFullySelected &&
                            workspace.isPartiallySelected
                          }
                          checked={workspace.isFullySelected}
                          onClick={() => {
                            const queuesOfThisWorkspace = workspace.queues.map(
                              ({ id }) => id
                            );
                            const updatedQueues = workspace.isFullySelected
                              ? selectedQueues.filter(
                                  id => !queuesOfThisWorkspace.includes(id)
                                )
                              : [...selectedQueues, ...queuesOfThisWorkspace];
                            handleSelect(updatedQueues);
                          }}
                          sx={{ p: 0 }}
                        />
                      </ListItemIcon>
                    ) : null}
                    <ListItemButton
                      disableGutters={dense}
                      onClick={() =>
                        setExpandedWorkspaces(prev =>
                          prev.includes(workspace.id)
                            ? prev.filter(id => id !== workspace.id)
                            : [...prev, workspace.id]
                        )
                      }
                      sx={dense ? { p: 0, pl: 1 } : {}}
                      disabled={disabled}
                    >
                      <Typography
                        mr="auto"
                        variant="body2"
                        sx={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {isSearchActive
                          ? highlightSearchedValue(workspace.name, search)
                          : workspace.name}
                      </Typography>

                      {expandedWorkspaces.includes(workspace.id) ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      )}
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={expandedWorkspaces.includes(workspace.id)}>
                    <List disablePadding dense sx={{ pb: 1 }}>
                      {workspace.queues.map(queue => {
                        const doesAlreadyExist = selectedQueuesSet.has(
                          queue.id
                        );
                        return (
                          <ListItem
                            disablePadding
                            disableGutters
                            key={queue.id}
                            dense
                          >
                            <ListItemButton
                              disabled={disabled}
                              disableRipple
                              onClick={() => {
                                if (!multiSelect) {
                                  handleSelect(
                                    selectedQueues.includes(queue.id)
                                      ? []
                                      : [queue.id]
                                  );
                                } else {
                                  const updatedQueues = doesAlreadyExist
                                    ? selectedQueues.filter(
                                        id => id !== queue.id
                                      )
                                    : selectedQueues.concat(queue.id);

                                  handleSelect(updatedQueues);
                                }
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 0, mr: 2 }}>
                                <Checkbox
                                  disabled={disabled}
                                  checked={doesAlreadyExist}
                                  sx={{ p: 0 }}
                                />
                              </ListItemIcon>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {isSearchActive
                                  ? highlightSearchedValue(queue.name, search)
                                  : queue.name}
                              </Typography>
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                </Stack>
              );
            })}
          </List>
        </Stack>
      )}
    </Stack>
  );
};
